import { useQuery } from '@tanstack/react-query'
import { getMe } from '../services/api'
import type { Me, RoleKey } from '../types'

/**
 * Current authenticated principal (roles + tenant scope), resolved from
 * GET /api/auth/me. Cached for the session.
 */
export function useMe() {
  return useQuery<Me>({
    queryKey: ['me'],
    queryFn: getMe,
    staleTime: 1000 * 60 * 10,
    // Don't retry auth errors (401/403) — the answer won't change.
    retry: (count, err) => {
      const status = (err as { response?: { status?: number } })?.response?.status
      if (status === 401 || status === 403) return false
      return count < 1
    },
  })
}

export function useRole() {
  const { data: me, isLoading } = useMe()

  const hasRole = (...roles: RoleKey[]): boolean =>
    !!me && me.roles.some((r) => roles.includes(r))

  const canAccessTenant = (tenantId: string): boolean => {
    if (!me) return false
    return me.allowedTenantIds === '*' || me.allowedTenantIds.includes(tenantId)
  }

  return { me, isLoading, hasRole, canAccessTenant }
}

/** True when the principal is entitled to a plan feature (internal users unlimited). */
export function useFeature(feature: string): boolean {
  const { data: me } = useMe()
  const ent = me?.entitlements
  return !!ent && (ent.unlimited || ent.features?.[feature] === true)
}
